import { type ExpenseCategoryDefinition, type ExpenseCategory } from '@/types/ExpenseCategory';
import { v4 as uuidv4 } from 'uuid';

/** Predefined expense category definitions — these cannot be deleted or renamed by users */
export const PREDEFINED_EXPENSE_CATEGORIES: ExpenseCategoryDefinition[] = [
  {
    id: 'parking',
    name: 'Parking',
    defaultScope: 'all',
    defaultMethod: 'at_cost',
    standardRateAvailable: null,
  },
  {
    id: 'mileage',
    name: 'Mileage',
    defaultScope: 'all',
    defaultMethod: 'standard_rate',
    standardRateAvailable: 'irs_mileage',
  },
  {
    id: 'lodging',
    name: 'Lodging',
    defaultScope: 'travel_only',
    defaultMethod: 'client_provided',
    standardRateAvailable: 'gsa_lodging',
  },
  {
    id: 'airfare',
    name: 'Flights / Airfare',
    defaultScope: 'travel_only',
    defaultMethod: 'at_cost',
    standardRateAvailable: null,
  },
  {
    id: 'ground_transport',
    name: 'Ground Transport',
    defaultScope: 'travel_only',
    defaultMethod: 'at_cost',
    standardRateAvailable: null,
  },
  {
    id: 'shipping_cartage',
    name: 'Shipping / Cartage',
    defaultScope: 'travel_only',
    defaultMethod: 'at_cost',
    standardRateAvailable: null,
  },
];

/** Build the default expense categories array from predefined definitions */
export function getDefaultExpenseCategories(): ExpenseCategory[] {
  return PREDEFINED_EXPENSE_CATEGORIES.map((def) => ({
    id: def.id,
    name: def.name,
    scope: def.defaultScope,
    method: def.defaultMethod,
    standardRateType: def.standardRateAvailable ?? undefined,
    rate: null,
    isCustom: false,
  }));
}

/** Create a new blank custom category */
export function createCustomExpenseCategory(overrides?: Partial<ExpenseCategory>): ExpenseCategory {
  return {
    id: uuidv4(),
    name: '',
    scope: 'all',
    method: 'at_cost',
    rate: null,
    isCustom: true,
    ...overrides,
  };
}

/** Get the predefined definition for a category by id, or null if custom */
export function getPredefinedDefinition(categoryId: string): ExpenseCategoryDefinition | null {
  return PREDEFINED_EXPENSE_CATEGORIES.find((d) => d.id === categoryId) ?? null;
}